import Polygon from 'ol/geom/Polygon';
import polygonClipping, { type MultiPolygon as ClippingMultiPolygon } from 'polygon-clipping';
import type { ClassificationScenario, LayerModel } from '../core/types';
import { activeArea } from './classification';
import { entityViewEntities } from './entityView';
import { measureArea } from './measurement';
import { asClipping, significant, type MixedClassificationAssignments, type MixedThematicPiece } from './mixedThematic';
import { addScenarioRow, finishScenarioRow, fragmentsGeometry, scenarioAccumulator, type ScenarioAccumulator, type ScenarioAssignments, type ScenarioFlow } from './scenarios';

/**
 * V1.9.0: a scenario over the Suerte + Áreas parciales view. «Actual» is that mixed view as it is (non-overlapping
 * pieces: Áreas parciales with a value + remnant of the Suerte). The proposal is laid over those pieces with this
 * precedence, highest first:
 *
 *   1. Tablón propuesto — its whole active surface takes the proposed value;
 *   2. Suerte propuesta — the rest of the Suerte's active surface (its Áreas parciales included) takes it;
 *   3. Área parcial (Actual) — keeps its own value where nothing is proposed;
 *   4. Suerte (Actual) — the remnant keeps the Suerte value.
 *
 * Every Actual piece is split by the proposed Tablones it crosses (polygon clipping, only for Suertes that have both
 * an exception and a proposed Tablón); the parts are fitted to the active fragment areas, so Actual and Propuesta add
 * up to the same total and the Actual side equals the mixed map. Nothing is stored.
 */
const { difference, intersection, union } = polygonClipping;
const EPSILON_HA = 1e-9;

export interface ScenarioPartialAssignments extends ScenarioAssignments {
  /** Classified fragments of the mixed view (DisplaySet ∩ ThemeTarget), unchanged by the scenario. */
  readonly subsetFragmentIds: ReadonlySet<string>;
}

const sum = (values: readonly number[]): number => values.reduce((total, value) => total + value, 0);
const hectares = (parts: ClippingMultiPolygon): number => sum(parts.map((part) => measureArea(new Polygon(part)).hectares));

/** Non-negative parts scaled to add up to `total` (clipping and fragment areas differ by measurement residues). */
function fit(parts: readonly number[], total: number, fallback: number): number[] {
  const clean = parts.map((value) => Math.max(0, value)); const current = sum(clean);
  if (current <= EPSILON_HA) return clean.map((_, index) => index === fallback ? Math.max(0, total) : 0);
  return clean.map((value) => value * total / current);
}

/** Index that takes measurement residues: the Suerte remnant, or the largest exception when they cover the whole Suerte. */
const residueIndex = (pieces: readonly MixedThematicPiece[]): number => {
  const remnant = pieces.findIndex(({ source }) => source === 'base');
  return remnant >= 0 ? remnant : pieces.reduce((best, piece, index) => piece.areaHa > pieces[best]!.areaHa ? index : best, 0);
};

/**
 * How some fragments of a split Suerte (e.g. one Tablón) divide among its Actual pieces: each exception measured
 * inside them, the rest is the Suerte remnant; fitted to their active area.
 */
function sharesIn(model: LayerModel, pieces: readonly MixedThematicPiece[], fragments: readonly string[]) {
  const geometry = fragmentsGeometry(model, fragments); const polygons = geometry.getPolygons().map((polygon) => asClipping(polygon));
  const clip = polygons.length ? union(polygons[0]!, ...polygons.slice(1)) : [];
  const areaHa = activeArea(model, fragments);
  const raw = pieces.map((piece) => piece.source === 'subarea' && clip.length ? hectares(intersection(asClipping(piece.geometry), clip)) : 0);
  const remnant = pieces.findIndex(({ source }) => source === 'base');
  if (remnant >= 0) raw[remnant] = areaHa - sum(raw);
  return { geometry, clip, areaHa, shares: fit(raw, areaHa, residueIndex(pieces)) };
}

const byCategory = (pieces: readonly MixedThematicPiece[], shares: readonly number[], into = new Map<string, number>()): Map<string, number> => {
  pieces.forEach((piece, index) => { const area = shares[index]!; if (area > EPSILON_HA) into.set(piece.category, (into.get(piece.category) ?? 0) + area); });
  return into;
};

/**
 * Actual and Propuesta (ha by category) of some fragments of ONE Suerte split by Áreas parciales — a Tablón selected
 * while «Ver por» is Suertes, for the preview and the Inspector — with the same measure as the composition.
 */
export function partialFragmentCategories(model: LayerModel, pieces: readonly MixedThematicPiece[], scenario: ClassificationScenario | null, fragments: readonly string[]): { readonly actual: ReadonlyMap<string, number>; readonly proposed: ReadonlyMap<string, number> } {
  const value = (id: string): string | undefined => { const fragment = model.fragmentsById.get(id); return fragment && scenario ? scenario.overrides.tablon[fragment.tablonId] ?? scenario.overrides.suerte[fragment.suerteId] : undefined; };
  const actual = fragments.length ? byCategory(pieces, sharesIn(model, pieces, fragments).shares) : new Map<string, number>();
  const plain = fragments.filter((id) => value(id) === undefined);
  const proposed = plain.length ? byCategory(pieces, sharesIn(model, pieces, plain).shares) : new Map<string, number>();
  fragments.forEach((id) => { const category = value(id); if (category !== undefined) proposed.set(category, (proposed.get(category) ?? 0) + activeArea(model, [id])); });
  return { actual, proposed };
}

/** A Suerte split by Áreas parciales, with the scenario laid over its pieces. */
function addSplitRow(acc: ScenarioAccumulator, model: LayerModel, mixed: MixedClassificationAssignments, row: { readonly id: string; readonly fragmentIds: readonly string[] }, actualPieces: readonly MixedThematicPiece[], scenario: ClassificationScenario): void {
  const suerteValue = scenario.overrides.suerte[row.id];
  const tablonValue = (fragmentId: string): string | undefined => { const tablonId = model.fragmentsById.get(fragmentId)?.tablonId; return tablonId === undefined ? undefined : scenario.overrides.tablon[tablonId]; };
  const byTablon = new Map<string, string[]>();
  row.fragmentIds.forEach((id) => { if (tablonValue(id) === undefined) return; const tablonId = model.fragmentsById.get(id)!.tablonId; byTablon.set(tablonId, [...(byTablon.get(tablonId) ?? []), id]); });
  const base = mixed.categoryByEntityId.get(row.id) ?? 'Sin valor';
  row.fragmentIds.forEach((id) => {
    const proposed = tablonValue(id) ?? suerteValue;
    acc.categoryByFragmentId.set(id, proposed ?? mixed.categoryByFragmentId.get(id) ?? base);
    acc.hasValueByFragmentId.set(id, proposed !== undefined || (mixed.hasValueByFragmentId.get(id) ?? false));
  });
  const changed = suerteValue !== undefined || byTablon.size > 0;
  acc.hasValueByEntityId.set(row.id, changed || (mixed.hasValueByEntityId.get(row.id) ?? false));
  if (!changed) {
    finishScenarioRow(acc, row.id, actualPieces.map((piece) => ({ entityId: row.id, from: piece.category, to: piece.category, areaHa: piece.areaHa })), actualPieces, base);
    return;
  }
  const fallback = residueIndex(actualPieces);
  const tablones = [...byTablon].map(([tablonId, fragments]) => ({ tablonId, value: scenario.overrides.tablon[tablonId]!, ...sharesIn(model, actualPieces, fragments) }));
  const rest = row.fragmentIds.filter((id) => tablonValue(id) === undefined);
  const restHa = activeArea(model, rest);
  const remaining = fit(actualPieces.map((piece, index) => piece.areaHa - sum(tablones.map(({ shares }) => shares[index]!))), restHa, fallback);
  const flows: ScenarioFlow[] = []; const pieces: MixedThematicPiece[] = [];
  const flow = (from: string, to: string, areaHa: number): void => { if (areaHa > EPSILON_HA) flows.push({ entityId: row.id, from, to, areaHa }); };
  tablones.forEach((tablon) => {
    actualPieces.forEach((piece, index) => flow(piece.category, tablon.value, tablon.shares[index]!));
    pieces.push({ parentSuerteId: row.id, source: 'scenario', sourceId: tablon.tablonId, category: tablon.value, hasValue: true, geometry: tablon.geometry, areaHa: tablon.areaHa });
  });
  if (rest.length && suerteValue !== undefined) {
    actualPieces.forEach((piece, index) => flow(piece.category, suerteValue, remaining[index]!));
    pieces.push({ parentSuerteId: row.id, source: 'scenario', sourceId: row.id, category: suerteValue, hasValue: true, geometry: fragmentsGeometry(model, rest), areaHa: restHa });
  } else if (rest.length) {
    const cut = tablones.map(({ clip }) => clip).filter((clip) => clip.length);
    actualPieces.forEach((piece, index) => {
      const areaHa = remaining[index]!; if (areaHa <= EPSILON_HA) return;
      flow(piece.category, piece.category, areaHa);
      const geometry = cut.length ? significant(difference(asClipping(piece.geometry), ...cut))?.geometry : piece.geometry;
      if (geometry) pieces.push({ ...piece, geometry, areaHa });
    });
  }
  finishScenarioRow(acc, row.id, flows, pieces);
}

let memo: { readonly key: readonly unknown[]; readonly result: ScenarioPartialAssignments } | null = null;

/** The mixed (Suerte + Áreas parciales) classification with a scenario applied. Memoized by (model, mixed view, scenario). */
export function scenarioPartialAssignments(model: LayerModel, mixed: MixedClassificationAssignments, scenario: ClassificationScenario): ScenarioPartialAssignments {
  const key = [model, mixed, scenario];
  if (memo && memo.key.every((value, index) => value === key[index])) return memo.result;
  const acc = scenarioAccumulator();
  entityViewEntities(model, 'suerte', mixed.subsetFragmentIds).forEach((row) => {
    const pieces = mixed.piecesBySuerteId.get(row.id);
    if (pieces) addSplitRow(acc, model, mixed, row, pieces, scenario);
    else addScenarioRow(acc, model, row, mixed.categoryByEntityId.get(row.id) ?? 'Sin valor', mixed.hasValueByEntityId.get(row.id) ?? false, scenario);
  });
  const result: ScenarioPartialAssignments = { ...acc, actualByFragmentId: mixed.categoryByFragmentId, subsetFragmentIds: mixed.subsetFragmentIds };
  memo = { key, result };
  return result;
}
